<template>
  <section class="container-fluid d-flex justify-content-center px-3">
    <!-- Loading State -->
    <div v-if="isLoading" class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="col-lg-6">
        <div class="skeleton-image"></div>
      </div>
      <div class="col-lg-6 my-4 my-lg-0">
        <div class="skeleton-content">
          <div class="skeleton-title"></div>
          <div class="skeleton-tags d-flex gap-2">
            <div class="skeleton-tag"></div>
            <div class="skeleton-tag"></div>
          </div>
          <div class="skeleton-description"></div>
          <div class="skeleton-price"></div>
          <div class="skeleton-button"></div>
        </div>
      </div>
    </div>

    <!-- Product Content -->
    <div v-else-if="product" class="container row col-12 pb-5 d-flex justify-content-lg-center mt-5 pt-5">
      <!-- Product Image -->
      <div class="col-lg-6 px-0 pe-lg-4 product-img-wrapper d-flex justify-content-start">
        <div 
          class="zoom-container mb-3 w-100"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
          ref="zoomContainer"
        >
          <img
            :src="product.image || require('@/assets/images/zapiola-logo.png')"
            :alt="product.name"
            class="zoom-image"
            :style="zoomStyle"
            ref="zoomImage"
          />
        </div>
      </div>

      <!-- Product Info -->
      <div class="col-lg-6 my-4 my-lg-0 px-0">
        <!-- Admin Edit Button -->
        <router-link v-if="isAdmin" :to="`/admin/productos/editar/${product.id}`" class="btn btn-primary mb-3">
          <i class="fas fa-edit me-2"></i>
          Editar Producto
        </router-link>

        <!-- Product Title & Tags -->
        <h1 class="display-4 fw-bold">{{ product.name }}</h1>
        <div class="d-flex tags gap-2">
          <span class="tag">{{ product.category_name }}</span>
          <span v-if="product.brand" class="tag">{{ product.brand }}</span>
        </div>

        <!-- Volume/Size Info -->
        <div v-if="product.volume || product.dimensions" class="mt-4">
          <span class="fs-5">
            <i class="fas fa-box me-2"></i>
            {{ product.volume || product.dimensions }}
          </span>
        </div>

        <!-- Description -->
        <div v-if="product.description" class="mt-4 fs-5">
          <p>{{ product.description }}</p>
        </div>

        <div v-else class="mt-4">
          <p class="text-muted fs-5">No hay descripción disponible para este producto.</p>
        </div>

        <!-- Category Specific Details -->
        <div class="mt-4">
          <!-- Indoor -->
          <div v-if="product.category_name === 'Articulos indoor'" class="features">
            <h3 class="fs-5 mb-3">Características:</h3>
            <ul class="list-unstyled">
              <li v-if="product.dimensions" class="mb-2">
                <i class="fas fa-expand-arrows-alt me-2 text-primary"></i>
                Dimensiones: {{ product.dimensions }}
              </li>
            </ul>
          </div>
        </div>

        <!-- Price Section -->
        <div class="price-section mt-4">
          <div class="d-flex align-items-baseline gap-3">
            <h4 class="fs-5 mb-0">Precio:</h4>
            <div class="d-flex align-items-center gap-2">
              <p v-if="product.discount > 0" class="original-price mb-0 text-muted text-decoration-line-through">
              ${{ formatPrice(product.price) }}
              </p>
              <p class="fs-3 fw-bold mb-0">${{ formatDiscountedPrice }}</p>
              <span v-if="product.discount > 0" class="discount-badge">
              -{{ product.discount }}%
              </span>
            </div>
          </div>
        </div>

        <!-- Stock Info -->
        <div class="stock-section mt-3">
          <p class="mb-0" :class="stockStatusClass">
            <i :class="stockIconClass" class="me-2"></i>
            {{ stockText }}
          </p>
        </div>

        <!-- Add to Cart -->
        <div class="d-flex gap-3 align-items-center mt-4">
          <div class="quantity-selector d-flex align-items-center gap-3">
            <button 
              class="btn btn-outline-light"
              @click="decrementQuantity"
              :disabled="quantity <= 1"
            >
              <i class="fas fa-minus"></i>
            </button>
            <span class="fs-5">{{ quantity }}</span>
            <button 
              class="btn btn-outline-light"
              @click="incrementQuantity"
              :disabled="quantity >= product.stock"
            >
              <i class="fas fa-plus"></i>
            </button>
          </div>
          <button 
            class="btn btn-light px-5 py-3" 
            @click="handleAddToCart"
            :disabled="product.stock === 0"
          >
            <i class="fas fa-shopping-cart me-2"></i>
            Añadir al Carrito
          </button>
        </div>
      </div>
    </div>

    <!-- No Product Found -->
    <div v-else class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="text-center">
        <i class="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
        <h3>Producto no encontrado</h3>
        <p class="text-muted">El producto que buscas no existe o no está disponible.</p>
        <router-link to="/productos" class="btn btn-primary mt-3">
          <i class="fas fa-arrow-left me-2"></i>
          Volver a Productos
        </router-link>
      </div>
    </div>
  </section>

  <!-- Related Products Section -->
  <section v-if="product && !isLoading" class="container mt-4 mb-5">
    <div class="row">
      <div class="col-12">
        <h2 class="fw-bold display-6 text-center mb-4">
          <span class="text-gradient-primary">Productos relacionados</span>
        </h2>
        <!-- Show loading when fetching related products -->
        <ProductsCarousel 
          :products="relatedProducts" 
          :isLoading="isLoadingRelated"
        />
        <div v-if="!isLoadingRelated && relatedProducts.length === 0" class="text-center py-4">
          <p class="text-muted">No hay productos relacionados disponibles</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from "vue-toastification";
import { getProduct, getRelatedProducts } from "@/services/products"; // Importar la nueva función
import { formatPrice } from "@/helpers/product";
import ProductsCarousel from '@/components/ProductsCarousel.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();
const isAdmin = localStorage.getItem('isAdmin') === 'true';
const product = ref(null);
const isLoading = ref(true);
const quantity = ref(1);
const error = ref(null);

// Zoom functionality
const zoomContainer = ref(null);
const zoomImage = ref(null);
const zoomStyle = ref({});

const handleMouseMove = (e) => {
  const { left, top, width, height } = zoomContainer.value.getBoundingClientRect();
  const x = (e.clientX - left) / width;
  const y = (e.clientY - top) / height;

  zoomStyle.value = {
    transform: 'scale(2)',
    transformOrigin: `${x * 100}% ${y * 100}%`
  };
};

const handleMouseLeave = () => {
  zoomStyle.value = {
    transform: 'scale(1)',
    transformOrigin: 'center center'
  };
};

// Computed properties
const formatDiscountedPrice = computed(() => {
  if (!product.value) return 0;
  if (!product.value.discount) return formatPrice(product.value.price);
  
  const discountAmount = (product.value.price * product.value.discount) / 100;
  return formatPrice(product.value.price - discountAmount);
});

const stockStatusClass = computed(() => {
  if (!product.value) return '';
  if (!product.value.isAvailable) return 'text-danger';
  if (product.value.stock <= 5) return 'text-warning';
  return 'text-success';
});

const stockIconClass = computed(() => {
  if (!product.value) return '';
  if (!product.value.isAvailable) return 'fas fa-times-circle';
  if (product.value.stock <= 5) return 'fas fa-exclamation-circle';
  return 'fas fa-check-circle';
});

const stockText = computed(() => {
  if (!product.value) return '';
  if (product.value.stock === 0) return 'Sin stock';
  if (product.value.stock <= 5) return `¡Últimas ${product.value.stock} unidades!`;
  return `${product.value.stock} unidades disponibles`;
});

// Methods
const incrementQuantity = () => {
  if (quantity.value < product.value.stock) {
    quantity.value++;
  }
};

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
  }
};

const handleAddToCart = () => {
  // if (product.value.stock === 0) {
  //   toast.error('Producto no disponible');
  //   return;
  // }
  
  const cartItem = {
    id: product.value.id,
    name: product.value.name,
    price: product.value.price,
    discount: product.value.discount,
    image: product.value.image, // Cambiar imageUrl por image para mantener consistencia
    quantity: quantity.value
  };

  store.dispatch('addToCart', cartItem);
  toast.success('Producto añadido al carrito');
};

const productId = ref(null);

// Add state for related products
const relatedProducts = ref([]);
const isLoadingRelated = ref(true);

const loadProduct = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    console.log("Cargando producto ID:", productId.value);
    
    const data = await getProduct(productId.value);
    if (!data) {
      throw new Error('Producto no encontrado');
    }
    
    console.log("Producto cargado:", data);
    product.value = data;
    
    // Load related products after the main product is loaded
    loadRelatedProducts();
  } catch (err) {
    console.error('Error loading product:', err);
    error.value = err.message || 'Error al cargar el producto';
    // No redireccionar automáticamente para poder ver el error
    // Solo redireccionar si es un error 404
    if (err.status === 404) {
      router.push('/404');
    }
  } finally {
    isLoading.value = false;
  }
};

// Function to load related products using the new endpoint
const loadRelatedProducts = async () => {
  try {
    isLoadingRelated.value = true;
    
    const response = await getRelatedProducts(productId.value);
    
    if (response && response.results) {
      relatedProducts.value = response.results;
    } else if (Array.isArray(response)) {
      relatedProducts.value = response;
    } else {
      relatedProducts.value = [];
    }
  } catch (error) {
    console.error('Error loading related products:', error);
    relatedProducts.value = [];
  } finally {
    isLoadingRelated.value = false;
  }
};

// Modificar el watch para manejar correctamente los cambios de ID
watch(() => route.params.id, (newId) => {
  console.log("ID del producto cambiado:", newId);
  productId.value = newId;
  loadProduct();
  window.scrollTo(0, 0); // Scroll to top when product changes
}, { immediate: true });

// Simplificar onMounted para evitar la doble carga
onMounted(() => {
  document.title = 'Detalles del Producto';
  // No volver a cargar el producto aquí ya que el watcher lo hará
});
</script>

<style scoped>
.tag {
  background-color: var(--black-color-2);
  color: var(--white-color-2);
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: 500;
}

.product-img-wrapper {
  max-height: 600px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-container {
  overflow: hidden;
  border-radius: 12px;
  background: linear-gradient(
    to bottom,
    var(--dark-color),
    var(--dark-color-2)
  );
  position: relative;
  cursor: zoom-in;
  width: 100%;
}

.zoom-image {
  width: 100%;
  height: 100%;
  max-height: 600px;
  transition: transform 0.1s ease-out;
  object-fit: contain;
  display: block;
}

.discount-badge {
  background-color: var(--danger-color);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
}

.original-price {
  font-size: 1.2rem;
}

.quantity-selector {
  background-color: var(--black-color-2);
  padding: 0.5rem;
  border-radius: 8px;
}

.quantity-selector button {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Skeleton Styles */
.skeleton-image {
  width: 100%;
  height: 600px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 20px;
}

.skeleton-content {
  padding: 1rem;
}

.skeleton-title {
  height: 48px;
  width: 80%;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.skeleton-tags {
  margin: 1rem 0;
}

.skeleton-tag {
  height: 24px;
  width: 80px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-description {
  height: 100px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-price {
  height: 64px;
  width: 40%;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
  margin: 2rem 0;
}

.skeleton-button {
  height: 48px;
  width: 200px;
  background: linear-gradient(90deg, #333333 25%, #404040 50%, #333333 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 8px;
}

@keyframes loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .skeleton-image {
    height: 300px;
  }
  
  .product-img-wrapper {
    max-height: 500px;
    justify-content: flex-start !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  
  .zoom-image {
    max-height: 500px;
  }

  .zoom-container {
    width: 100%;
    margin-left: 0;
  }
}

/* Agregar estilos para las características específicas */
.features ul li {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  background: var(--black-color-2);
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.features ul li i {
  width: 24px;
  text-align: center;
}

</style>